import { ReactNode } from "react";
import styles from "./FieldRow.module.css";

interface Props {
  name: ReactNode;
  apiName: string;
  hint?: ReactNode;
  /**
   * Put the label ABOVE the control and give the control the whole card.
   *
   * For the rows whose control is a wide table rather than a scalar: five
   * columns in the 1fr half of the two-column grid reach their own horizontal
   * scrollbar while the short label leaves its 220px column mostly empty. The
   * rules are the ones this row already applies below 640px, no more.
   */
  stacked?: boolean;
  /** A control pinned to the label's right edge (a period toggle); the hint
   *  then runs under the whole label line. */
  aside?: ReactNode;
  children: ReactNode;
}

/**
 * Two-column field row (label + control) used throughout the Config page.
 *
 * Mockup: project-config.html L159-L165 — grid 220px / 1fr, dashed top
 * border between rows, label stack with human name + mono api-name + hint.
 * The dashed-divider behaviour lives in the parent SectionCard styles via
 * `.fieldRow + .fieldRow` so consumers don't need to thread an index in.
 */
export function FieldRow({
  name,
  apiName,
  hint,
  stacked,
  aside,
  children,
}: Props) {
  return (
    <div className={`${styles.fieldRow} ${stacked ? styles.stacked : ""}`}>
      {aside ? (
        <>
          <div className={`${styles.label} ${styles.labelAside}`}>
            <div>
              <div className={styles.name}>{name}</div>
              <div className={styles.apiName}>{apiName}</div>
            </div>
            {aside}
          </div>
          {hint && (
            <div className={`${styles.hint} ${styles.hintBelow}`}>{hint}</div>
          )}
        </>
      ) : (
        <div className={styles.label}>
          <div className={styles.name}>{name}</div>
          <div className={styles.apiName}>{apiName}</div>
          {hint && <div className={styles.hint}>{hint}</div>}
        </div>
      )}
      <div className={styles.input}>{children}</div>
    </div>
  );
}
